import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Straighten as UnitIcon,
} from '@mui/icons-material';
import PaginatedTable from '../components/common/PaginatedTable';
import { unitsApi } from '../api/units.api';
import { Unit } from '../types';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';

const emptyUnit: Partial<Unit> = {
  name: '',
  abbreviation: '',
};

const Units: React.FC = () => {
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Partial<Unit>>(emptyUnit);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);

  const load = useCallback(async () => {
    try {
      const res = await unitsApi.getAll();
      setUnits(res.data);
    } catch (err) {
      console.error('Error loading units:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpenCreate = () => {
    setSelectedUnit(emptyUnit);
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleOpenEdit = (unit: Unit) => {
    setSelectedUnit({ ...unit });
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (isEditing && selectedUnit.id) {
        await unitsApi.update(selectedUnit.id, selectedUnit);
      } else {
        await unitsApi.create(selectedUnit);
      }
      setModalOpen(false);
      load();
    } catch (err) {
      console.error('Error saving unit:', err);
    }
  };

  const handleDelete = async () => {
    try {
      await unitsApi.delete(deleteId);
      setDeleteDialogOpen(false);
      load();
    } catch (err) {
      console.error('Error deleting unit:', err);
    }
  };

  const handleChange = (field: string, value: any) => {
    setSelectedUnit((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UnitIcon sx={{ fontSize: 32, color: '#818CF8' }} />
          <Typography variant="h4">Unidades de Medida</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Nueva Unidad
        </Button>
      </Box>

      <PaginatedTable
        columns={[
          { id: 'id', label: 'ID', minWidth: 60 },
          { id: 'name', label: 'Nombre', sx: { fontWeight: 500 } },
          {
            id: 'abbreviation', label: 'Abreviatura',
            format: (value: string) => (
              <Chip label={value} size="small" variant="outlined" color="primary" />
            ),
          },
          {
            id: 'isActive', label: 'Estado',
            format: (value: boolean) => (
              <Chip
                label={value ? 'Activo' : 'Inactivo'}
                size="small"
                color={value ? 'success' : 'default'}
              />
            ),
          },
        ]}
        rows={units}
        searchFields={['name', 'abbreviation']}
        exportFileName="unidades"
        emptyMessage="No hay unidades registradas"
        renderActions={(u) => (
          <>
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(u)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => { setDeleteId(u.id); setDeleteDialogOpen(true); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      />

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{isEditing ? 'Editar Unidad' : 'Nueva Unidad'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Nombre"
                value={selectedUnit.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                placeholder="Ej: Kilogramos"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Abreviatura"
                value={selectedUnit.abbreviation || ''}
                onChange={(e) => handleChange('abbreviation', e.target.value)}
                required
                placeholder="Ej: kg"
                helperText="Abreviatura corta para mostrar en tablas"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!selectedUnit.name || !selectedUnit.abbreviation}
          >
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Unidad"
        message="¿Estas seguro de que deseas eliminar esta unidad de medida?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default Units;
